import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../shared/apiClient';
import { usePermission } from '../shared/hooks/usePermission';
import './AuditLogSection.css';

type AuditRow = {
  id: number;
  action: string;
  resource_type?: string | null;
  resource_id?: string | null;
  created_at: string;
  user_display_name?: string | null;
  details?: unknown;
};

export function AuditLogSection() {
  const { currentOrg } = useAuth();
  const { allowed } = usePermission('audit.view');
  const [items, setItems] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentOrg?.id || !allowed) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/audit-log?page=1&pageSize=25', {
        headers: {
          'X-Organization-Id': currentOrg.id,
        },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || 'Failed to load audit log');
      }
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.id, allowed]);

  useEffect(() => {
    load();
  }, [load]);

  if (!allowed) return null;

  return (
    <section className="audit-log-section" aria-labelledby="audit-log-heading">
      <h2 id="audit-log-heading" className="section-title audit-log-section__title">
        <ClipboardList size={20} aria-hidden />
        Audit log
      </h2>
      <p className="section-description">
        Recent security-relevant actions (Owner / Administrator).
      </p>
      {loading ? (
        <div className="audit-log-section__loading" role="status" aria-live="polite">
          <Loader2 className="animate-spin" size={18} aria-hidden />
          <span>Loading…</span>
        </div>
      ) : error ? (
        <p className="auth-error">{error}</p>
      ) : items.length === 0 ? (
        <p className="audit-log-section__empty">No entries yet.</p>
      ) : (
        <div className="audit-log-table-wrap">
          <table className="audit-log-table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Action</th>
                <th scope="col">Resource</th>
                <th scope="col">User</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td className="audit-log-table__when">
                    {new Date(row.created_at).toLocaleString()}
                  </td>
                  <td className="audit-log-table__action">{row.action}</td>
                  <td className="audit-log-table__resource">
                    {row.resource_type || '—'}
                    {row.resource_id ? ` #${row.resource_id}` : ''}
                  </td>
                  <td>{row.user_display_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
